export type FeatureFaq = {
  question: string
  answer: string
}

export type RelatedFeature = {
  label: string
  href: string
  description: string
}

export type PublicProductFeature = {
  slug: string
  eyebrow: string
  title: string
  description: string
  problemTitle: string
  problem: string[]
  image?: string
  imageAlt?: string
  workflow: Array<{ title: string; description: string }>
  capabilities: string[]
  limitations: string[]
  faqTitle?: string
  learningGuide?: { href: string; label: string }
  faq: FeatureFaq[]
  related: RelatedFeature[]
}

export const productFeatures: PublicProductFeature[] = [
  {
    slug: 'options-portfolio-analytics',
    eyebrow: 'Track and understand',
    title: 'Options portfolio analytics built around the positions you own',
    description:
      'Track stocks, single options, and multi-leg strategies in one portfolio, then understand P&L, exposure, and portfolio Greeks without rebuilding a spreadsheet.',
    problemTitle: 'Options positions make ordinary portfolio trackers break down',
    problem: [
      'A brokerage account can show contracts and fills, but it rarely tells the complete story of a strategy across its legs, adjustments, premiums, and closing activity.',
      'Optioneer groups the position history that belongs together so you can see what you own now, what has already been realized, and how the portfolio is exposed.',
    ],
    image: '/screenshots/dashboard.webp',
    imageAlt: 'Optioneer portfolio dashboard with options positions, performance, and allocation',
    workflow: [
      {
        title: 'Build the portfolio',
        description:
          'Add trades manually, import CSV activity, or use read-only Broker Sync when your paid plan and brokerage are eligible.',
      },
      {
        title: 'Keep strategies together',
        description:
          'Track stock and option legs as single-leg, multi-leg, Wheel, or custom strategies instead of isolated contracts.',
      },
      {
        title: 'Understand the exposure',
        description:
          'Review unrealized and realized P&L, allocation, and Delta, Gamma, Theta, and Vega when reliable market data is available.',
      },
      {
        title: 'Learn from closed outcomes',
        description:
          'Use Your Edge to compare the strategies and underlyings that have actually contributed to realized results.',
      },
    ],
    capabilities: [
      'Stock, single-option, and multi-leg options tracking',
      '20 predefined strategy types plus custom strategies',
      'Realized and unrealized P&L with position activity history',
      'Portfolio and position Delta, Gamma, Theta, and Vega when data is available',
      'Multiple portfolios, cash tracking, CSV import, and CSV export',
      'Shareable trade cards for selected positions and outcomes',
    ],
    limitations: [
      'Optioneer is designed for US-listed stocks and options; instrument and market-data coverage can vary.',
      'Greeks and market values are estimates based on available data, not execution prices or guarantees.',
      'Broker Sync is read-only and requires active paid Premium. It is not included during the trial.',
      'Optioneer does not place, modify, cancel, or automatically execute trades.',
    ],
    faq: [
      {
        question: 'Can Optioneer track multi-leg options strategies?',
        answer:
          'Yes. Optioneer tracks predefined multi-leg strategies and custom strategies, keeping related legs and activity together in the portfolio.',
      },
      {
        question: 'Which portfolio Greeks does Optioneer show?',
        answer:
          'Optioneer can show Delta, Gamma, Theta, and Vega at position and portfolio level when the required market data is available and reliable.',
      },
      {
        question: 'How can I add portfolio data?',
        answer:
          'You can enter trades manually, import supported CSV activity, or connect an eligible brokerage through read-only Broker Sync with active paid Premium.',
      },
    ],
    related: [
      {
        label: 'Broker Sync',
        href: '/broker-sync',
        description:
          'Keep eligible positions, cash, and transaction history current with read-only access.',
      },
      {
        label: 'Your Edge',
        href: '/features/your-edge',
        description: 'See which strategies and underlyings have actually worked for you.',
      },
      {
        label: 'Scenario Lab',
        href: '/features/scenario-lab',
        description: 'Test a hypothetical trade against the portfolio you already track.',
      },
    ],
  },
  {
    slug: 'performance-calendar',
    eyebrow: 'Performance Calendar',
    title: 'See your portfolio performance day by day.',
    description:
      'See daily P&L in a monthly view. Open a day for the details, or switch between portfolios to review them separately.',
    problemTitle: 'See the month, then look closer',
    problem: [
      'A monthly total is a starting point. Performance Calendar shows the daily portfolio P&L behind it, with positive, negative, and unchanged days kept distinct from unavailable data.',
    ],
    workflow: [
      {
        title: 'Review the month',
        description:
          'See monthly P&L and a cash-flow-adjusted return alongside daily results. Up and down days describe portfolio performance, not winning or losing trades.',
      },
      {
        title: 'Open a day',
        description:
          'Review opening and closing account values, net deposits and withdrawals, and the available breakdown by asset class and portfolio. Select the day again or use Close to dismiss it.',
      },
      {
        title: 'Choose your portfolio',
        description:
          'Review one portfolio or All Portfolios. The combined view uses the available portfolios and identifies incomplete coverage when data is missing.',
      },
    ],
    capabilities: [],
    limitations: [
      'Calendar history depends on the activity, opening balances, and dated valuation data available in Optioneer. Dates may be unavailable when required data is missing.',
    ],
    faqTitle: 'Performance Calendar FAQs',
    faq: [
      {
        question: 'What does the calendar’s P&L measure?',
        answer:
          'Daily portfolio P&L is the change in account value, including cash and valued holdings, adjusted for external deposits, withdrawals, and transfers. It includes changes in open positions, rather than only realized results from closed trades. Option premium received is not automatically profit. Monthly P&L covers the available period; monthly percentage returns adjust for the timing of external cash flows.',
      },
      {
        question: 'How much historical performance can I see?',
        answer:
          'You can review dates with the required saved activity and valuation history. A reliable starting baseline and a later covered close are needed. Manual entry, a positions-only CSV, or connecting a broker does not automatically recreate every past day.',
      },
      {
        question: 'Do I need to connect a broker?',
        answer:
          'No. Eligible manually tracked portfolios can also have calendar results when the required dated activity, cash, holdings, and valuations are available. Broker Sync is read-only, requires active paid Premium, and is not available during the trial. Performance Calendar access is subject to its separate limited rollout.',
      },
    ],
    related: [
      {
        label: 'Portfolio Analytics',
        href: '/features/options-portfolio-analytics',
        description: 'Review your positions, P&L, exposure, and portfolio Greeks.',
      },
      {
        label: 'Your Edge',
        href: '/features/your-edge',
        description: 'Explore realized results by strategy and underlying.',
      },
    ],
  },
  {
    slug: 'wheel-tracker',
    eyebrow: 'Track the lifecycle',
    title: 'Track the complete Wheel lifecycle in one view',
    description:
      'Follow cash-secured puts, assignment, shares, covered calls, premium, and exits as one connected Wheel cycle.',
    problemTitle: 'The Wheel is a lifecycle, not a list of unrelated trades',
    problem: [
      'When a short put becomes shares and those shares become collateral for a covered call, a contract-by-contract ledger loses the context that matters.',
      'Optioneer keeps the position stages and cash flows connected so recorded share costs, collected premium, and the current stage remain understandable.',
    ],
    image: '/native-welcome/optimized/native-welcome-wheel.webp',
    imageAlt: 'Optioneer Wheel tracker showing an options strategy lifecycle',
    workflow: [
      {
        title: 'Start with the short put',
        description: 'Record the cash-secured put, premium, collateral, expiration, and outcome.',
      },
      {
        title: 'Carry assignment forward',
        description:
          'If assigned, continue the same lifecycle with shares rather than starting a disconnected position.',
      },
      {
        title: 'Track covered calls',
        description:
          'Add covered calls against the shares and follow premium, expiration, assignment, or a new call.',
      },
      {
        title: 'Review the full cycle',
        description:
          'See the connected activity and realized outcome when the shares leave the portfolio or the cycle closes.',
      },
    ],
    capabilities: [
      'Connected cash-secured put, assignment, shares, and covered-call stages',
      'Premium, collateral, break-even, and lifecycle activity tracking',
      'Open and completed Wheel cycles in the same portfolio workflow',
      'Roll workflows for eligible short puts and covered calls',
      'Lifecycle-aware realized results in Your Edge',
    ],
    limitations: [
      'Optioneer records and analyzes the lifecycle; it does not execute Wheel trades.',
      'A complete result depends on recording or importing the relevant assignments, expirations, closes, and share exits.',
      'Market values and Greeks depend on available quote data.',
      'An economic premium-adjusted basis is a tracking measure, not a determination of tax cost basis. Premium cash received is not automatically profit.',
    ],
    faq: [
      {
        question: 'Can Optioneer track the Wheel strategy?',
        answer:
          'Yes. It connects the short-put, assignment, share, covered-call, and exit stages so the Wheel can be reviewed as one lifecycle.',
      },
      {
        question: 'Does assignment end a Wheel cycle?',
        answer:
          'No. An assigned short put can continue into the share and covered-call stages. The realized lifecycle result remains open until the shares exit.',
      },
      {
        question: 'Can I compare a roll for a Wheel position?',
        answer:
          'Eligible short puts and covered calls can use Roll Assistant to compare current option-chain candidates. Candidates are not recommendations.',
      },
    ],
    related: [
      {
        label: 'Roll Assistant',
        href: '/features/roll-assistant',
        description: 'Compare eligible Wheel rolls using current option-chain data.',
      },
      {
        label: 'Your Edge',
        href: '/features/your-edge',
        description: 'Review completed lifecycle outcomes by strategy and underlying.',
      },
      {
        label: 'Wheel strategy guide',
        href: '/strategies/wheel-strategy',
        description: 'Learn how the strategy stages, risks, and tradeoffs fit together.',
      },
    ],
  },
  {
    slug: 'options-discovery',
    eyebrow: 'Find opportunities',
    title: 'Find options ideas with Discovery and reusable screens',
    description:
      'Explore strategy candidates from a configured universe of more than 2,500 stocks, narrow results with practical filters, and save screens. Coverage varies by scan and data availability.',
    problemTitle:
      'A useful options screen should narrow the market without pretending to make the decision',
    problem: [
      'Raw option chains are exhaustive, while generic stock screeners miss the contract-level details and strategy context options traders need.',
      'Discovery turns a broad universe into inspectable candidates. You still review the underlying, contract, risk, liquidity, and fit before acting.',
    ],
    image: '/native-welcome/optimized/native-welcome-discovery.webp',
    imageAlt: 'Optioneer Discovery interface with options strategy opportunities',
    workflow: [
      {
        title: 'Choose a strategy scan',
        description:
          'Start from an available options strategy view and define the market conditions you want to inspect.',
      },
      {
        title: 'Apply practical filters',
        description:
          'Narrow candidates by supported price, expiration, liquidity, return, and contract criteria.',
      },
      {
        title: 'Save the screen',
        description:
          'Premium members can save, rename, duplicate, update, and rerun up to 50 custom Discovery screens.',
      },
      {
        title: 'Test portfolio fit',
        description:
          'Take a candidate into Scenario Lab to see how the hypothetical trade changes the actual portfolio you selected.',
      },
    ],
    capabilities: [
      'A default universe of more than 2,500 US stocks, with coverage depending on the scan and available data',
      'Contract, expiration, liquidity, and return filters where supported',
      'Up to 50 saved custom screens for Premium members',
      'Rename, duplicate, update, delete, and compare saved screen runs',
    ],
    limitations: [
      'Discovery results are candidates, not trade recommendations or investment advice.',
      'Quote, chain, earnings, and volatility fields can be delayed, unavailable, or incomplete for some symbols.',
      'Review the live contract and broker order details before placing any trade yourself.',
    ],
    faq: [
      {
        question: 'Does Optioneer have an options screener?',
        answer:
          'Yes. Discovery provides options strategy scans and filters that help narrow a broad stock universe into contract candidates for further research.',
      },
      {
        question: 'Can I save custom options screens?',
        answer:
          'Yes. Premium members can save and manage up to 50 custom Discovery screens, then rerun or compare them later.',
      },
      {
        question: 'Does Discovery recommend a trade?',
        answer:
          'No. Discovery surfaces candidates that match selected criteria. It does not tell you what to buy or sell.',
      },
    ],
    related: [
      {
        label: 'Scenario Lab',
        href: '/features/scenario-lab',
        description: 'Test a Discovery candidate against your real portfolio before placing it.',
      },
      {
        label: 'Options Calculator',
        href: '/tools/options-profit-calculator',
        description: 'Model the payoff of a single option or multi-leg setup.',
      },
      {
        label: 'Discovery guide',
        href: '/learning/discovery',
        description: 'Understand filters, data quality, and how to evaluate scan results.',
      },
    ],
  },
  {
    slug: 'scenario-lab',
    eyebrow: 'Scenario Lab',
    title: 'Test a trade before you make it',
    description:
      'What would a possible trade change in the portfolio you already have? Add it hypothetically and compare the before-and-after estimates. Nothing here changes your real positions or places an order.',
    problemTitle: 'See the trade alongside what you already own',
    problem: [
      'You already have bullish exposure to SOFI and are considering another short put. Its payoff alone cannot tell you how much more exposure it adds to the SOFI shares and options you already hold.',
      'Scenario Lab starts with your selected real Optioneer portfolio, adds the proposed trade in a separate what-if scenario, and shows estimated changes to cash and exposure. You can inspect the trade without recording a real position.',
    ],
    workflow: [
      {
        title: 'Select the actual portfolio',
        description:
          'Choose the Optioneer portfolio whose current positions, cash, and exposure should be the baseline.',
      },
      {
        title: 'Build the hypothetical trade',
        description:
          'Add stock and option legs, quantities, expirations, strikes, and proposed prices without recording a live position.',
      },
      {
        title: 'Read before and after',
        description:
          'Compare cash and exposure, then inspect the trade’s payoff where supported. Delta, Gamma, Theta, and Vega explain sensitivity to price, time, and volatility.',
      },
      {
        title: 'Save and revisit',
        description:
          'Keep an analysis, reopen or duplicate it, and refresh its prices. Optional manual IBKR instructions are a later step; CSV export has stricter single-leg eligibility.',
      },
    ],
    capabilities: [
      'Hypothetical stock and option scenarios with up to 10 legs',
      'Payoff, break-even, maximum profit, and maximum loss analysis where defined',
      'Before-and-after cash estimates and supported stock-value concentration changes',
      'Per-stock Delta/Gamma and compatible Theta/Vega comparisons when reliable data is available',
      'Saved scenarios that can be reopened, duplicated, and refreshed',
      'Reviewable manual IBKR instructions and BasketTrader CSV export for eligible single-leg orders',
    ],
    limitations: [
      'Scenario Lab is hypothetical. It never changes the actual Optioneer portfolio or brokerage account.',
      'Calculated values are estimates based on the inputs and available market data; they are not forecasts.',
      'The target-price view shows the trade at expiration where supported; there are no arbitrary future-date or volatility-shock controls.',
      'Order preparation is a manual handoff. Optioneer does not connect a trading session or submit an order to IBKR.',
      'Scenario Lab is a Premium feature and does not provide investment advice.',
    ],
    faqTitle: 'Scenario Lab FAQs',
    learningGuide: {
      href: '/learning/scenario-lab',
      label: 'New to what-if analysis? Follow the Scenario Lab guide.',
    },
    faq: [
      {
        question: 'Can I test a trade before placing it?',
        answer:
          'Yes. Scenario Lab lets you model a hypothetical stock or options trade and compare it with the current portfolio you select.',
      },
      {
        question: 'Does Scenario Lab change my actual portfolio?',
        answer:
          'No. A scenario is separate from the real portfolio. Saving, refreshing, or deleting it does not add, edit, or close an actual position.',
      },
      {
        question: 'Can it calculate how a trade changes portfolio Greeks?',
        answer:
          'Yes, when reliable data is available. Delta and Gamma are compared for each stock; compatible Theta and Vega can be combined. These are sensitivities, not predictions or guaranteed outcomes.',
      },
      {
        question: 'Is cash after the trade my available buying power?',
        answer:
          'No. It is an estimated accounting cash balance at the assumed fill. Your broker determines available trading funds, margin, reserves, and currency conversion.',
      },
      {
        question: 'Can I test any future date or change implied volatility?',
        answer:
          'The current target-price control models supported trade outcomes at expiration. It does not reprice the portfolio at an arbitrary future date or apply a volatility shock. Saved price updates use available market observations.',
      },
      {
        question: 'Does Optioneer submit the trade to Interactive Brokers?',
        answer:
          'No. Supported scenarios can prepare manual instructions; a BasketTrader CSV is limited to eligible single-leg orders. You review and transmit the order yourself in IBKR TWS.',
      },
    ],
    related: [
      {
        label: 'Discovery',
        href: '/features/options-discovery',
        description: 'Find a candidate, then test whether it fits the portfolio.',
      },
      {
        label: 'Roll Assistant',
        href: '/features/roll-assistant',
        description: 'Open an eligible roll candidate as a scenario for a portfolio-level check.',
      },
      {
        label: 'Broker Sync',
        href: '/broker-sync',
        description: 'Keep an eligible portfolio current before using it as the scenario baseline.',
      },
    ],
  },
  {
    slug: 'roll-assistant',
    eyebrow: 'Roll Assistant',
    title: 'See what changes when you roll an option',
    description:
      'Rolling means closing the option you have and opening another one, usually with a different expiration, strike, or both. Compare what you might receive or pay, how much time you add, and how your exposure changes.',
    problemTitle: 'A larger credit comes with a different trade-off',
    problem: [
      'When you compare rolls, you are exchanging more time or a different strike and exposure for money received or paid. Liquidity and the remaining assignment or exercise risk matter too.',
      'Roll Assistant puts those alternatives side by side, starting with your existing position. More credit can mean more time committed or different exposure; it does not automatically mean a better trade or a profit.',
    ],
    workflow: [
      {
        title: 'Open an eligible position',
        description:
          'Launch Roll Assistant from a supported short put, covered or short call, long call, or long put position.',
      },
      {
        title: 'Choose what to change',
        description:
          'Add more time, move the strike where supported, or choose dates, strikes, and filters manually from the available option chain.',
      },
      {
        title: 'Compare the alternatives',
        description:
          'See days added, estimated money received or paid, assignment amounts where supported, stock exposure, and quote warnings. Keep, close, and roll are separate decisions.',
      },
      {
        title: 'See portfolio impact',
        description:
          'Preview one roll in Scenario Lab to see its hypothetical effect on the portfolio you already track before deciding what to do.',
      },
    ],
    capabilities: [
      'A clear summary of your existing option and available roll alternatives',
      'Practical intents plus detailed filters and transparent sorting',
      'Support for eligible short puts, covered or short calls, long calls, and long puts',
      'Estimated cash flow, days added, strike changes, and stock exposure from available quotes',
      'Mid, Natural, and Custom price assumptions, with liquidity and quote-quality warnings',
      'One-click handoff of a candidate to Scenario Lab',
    ],
    limitations: [
      'Roll Assistant does not recommend which roll you should make and does not rank a “best” trade.',
      'Grouped multi-leg positions and the anchor leg of a poor man’s covered call are not supported in the current roll workflow.',
      'Estimates require usable, consistent quote data and exclude fees and slippage. Quotes and assumed prices do not guarantee a fill; closed-market references are indicative only.',
      'Assignment amounts are contractual amounts, not broker margin or buying-power estimates. Adjusted or unverified terms can make these amounts unavailable.',
      'Optioneer does not submit, modify, cancel, or execute the roll.',
    ],
    faqTitle: 'Roll Assistant FAQs',
    learningGuide: {
      href: '/learning/roll-assistant',
      label: 'New to rolling? See the step-by-step Roll Assistant guide.',
    },
    faq: [
      {
        question: 'What does rolling an option mean?',
        answer:
          'You close your existing option and open another option, often at a different expiration, strike, or both. The new option has its own remaining risk.',
      },
      {
        question: 'Is a roll credit profit?',
        answer:
          'No. A credit is the estimated net money received from closing one option and opening another. It is not the profit on the existing trade, and the new option remains open.',
      },
      {
        question: 'Do I have to roll an in-the-money option?',
        answer:
          'No. In the money describes the relationship between the stock price and strike; it does not make rolling mandatory or assignment certain. Keeping, closing, and rolling have different consequences.',
      },
      {
        question: 'Does Roll Assistant recommend which roll I should make?',
        answer:
          'No. It applies transparent filters and sorting, but no candidate is scored or recommended. The decision remains yours.',
      },
      {
        question: 'Can Roll Assistant execute a roll?',
        answer:
          'No. It can send a candidate to Scenario Lab for analysis, but Optioneer never submits the order to a broker.',
      },
      {
        question: 'Why might my fill differ from the Mid estimate?',
        answer:
          'Mid is halfway between each option’s current bid and ask. Market prices can move, liquidity can be limited, and a broker may not fill at that price. Natural and Custom let you inspect other assumptions; fees and slippage are excluded.',
      },
    ],
    related: [
      {
        label: 'Scenario Lab',
        href: '/features/scenario-lab',
        description: 'Check an eligible candidate against portfolio exposure before acting.',
      },
      {
        label: 'Wheel Tracker',
        href: '/features/wheel-tracker',
        description: 'Keep short-put and covered-call rolls in the full Wheel lifecycle.',
      },
      {
        label: 'Options portfolio analytics',
        href: '/features/options-portfolio-analytics',
        description: 'Understand the current position and portfolio exposure first.',
      },
    ],
  },
  {
    slug: 'your-edge',
    eyebrow: 'Understand your results',
    title: 'See which strategies and underlyings have actually worked for you',
    description:
      'Use your own realized trade history to compare outcomes by strategy and underlying instead of relying on a generic backtest.',
    problemTitle: 'The useful question is what has worked in your portfolio',
    problem: [
      'A total return can hide whether results came from one symbol, one strategy, or a repeatable part of your process.',
      'Your Edge organizes realized outcomes from the activity recorded in Optioneer, making strengths and weak spots easier to inspect without turning history into a prediction.',
    ],
    image: '/screenshots/performance-analytics.webp',
    imageAlt: 'Optioneer performance analytics showing realized trading results',
    workflow: [
      {
        title: 'Keep portfolio history complete',
        description:
          'Record, import, or synchronize the position activity that should contribute to realized results.',
      },
      {
        title: 'Choose a period',
        description: 'Review 30-day, 90-day, year-to-date, one-year, or all-time results.',
      },
      {
        title: 'Compare what contributed',
        description:
          'Break realized outcomes down by strategy and underlying to see where gains and losses occurred.',
      },
      {
        title: 'Inspect lifecycle-aware results',
        description:
          'Wheel assignments remain part of an open lifecycle until the related shares exit, keeping incomplete cycles separate from realized outcomes.',
      },
    ],
    capabilities: [
      'Realized results grouped by strategy and underlying',
      '30D, 90D, YTD, 1Y, and all-time periods',
      'Lifecycle-aware treatment of Wheel assignments and share exits',
      'Open-cycle context kept separate from completed realized outcomes',
      'Direct connection to the portfolio activity that produced each result',
    ],
    limitations: [
      'Your Edge reflects the portfolio history available in Optioneer; missing or incorrect activity affects the analysis.',
      'Historical results do not predict future performance and are not a strategy recommendation.',
      'Open and unrealized positions are not presented as completed realized outcomes.',
    ],
    faq: [
      {
        question: 'What is Your Edge?',
        answer:
          'Your Edge is Optioneer’s realized-results analysis. It shows how strategies and underlyings contributed to the outcomes recorded in your portfolio history.',
      },
      {
        question: 'Is Your Edge a backtest?',
        answer:
          'No. It analyzes your own recorded results. It does not simulate a generic historical strategy or predict future returns.',
      },
      {
        question: 'How are Wheel assignments handled?',
        answer:
          'An assignment that creates shares remains part of an open lifecycle. The cycle is not treated as a completed realized outcome until the related shares exit.',
      },
    ],
    related: [
      {
        label: 'Options portfolio analytics',
        href: '/features/options-portfolio-analytics',
        description: 'Track the positions and activity that power Your Edge.',
      },
      {
        label: 'Performance Calendar',
        href: '/features/performance-calendar',
        description:
          'Your Edge breaks down realized results by strategy and underlying. Performance Calendar gives you a day-by-day view of portfolio P&L where available.',
      },
      {
        label: 'Wheel Tracker',
        href: '/features/wheel-tracker',
        description: 'Keep assignments and share exits connected across the full lifecycle.',
      },
      {
        label: 'Scenario Lab',
        href: '/features/scenario-lab',
        description: 'Use current portfolio context to test the next hypothetical trade.',
      },
    ],
  },
]

export const productFeatureBySlug = Object.fromEntries(
  productFeatures.map((feature) => [feature.slug, feature])
) as Record<string, PublicProductFeature>
